"use client";

import { Pause, Play } from "@phosphor-icons/react";
import type { Experimental_SpeechResult as SpeechResult } from "ai";
import {
  MediaControlBar,
  MediaController,
  MediaDurationDisplay,
  MediaMuteButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from "media-chrome/react";
import type { ComponentProps, CSSProperties, SVGProps } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

// ============================================================================
// Custom transport icons (lua-ui/icons/*.svg)
// ============================================================================
// fill="currentColor" so the glyphs inherit theme foreground in light + dark.

const SeekBackwardIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    aria-hidden
    fill="none"
    height="1em"
    viewBox="0 0 32 32"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <title>Seek backward</title>
    <path d="M5 16L12.5 11.6699V20.3301L5 16Z" fill="currentColor" />
    <path d="M17 16L24.5 11.6699V20.3301L17 16Z" fill="currentColor" />
  </svg>
);

const SeekForwardIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    aria-hidden
    fill="none"
    height="1em"
    viewBox="0 0 32 32"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <title>Seek forward</title>
    <path d="M15 16L7.5 20.3301L7.5 11.6699L15 16Z" fill="currentColor" />
    <path d="M27 16L19.5 20.3301V11.6699L27 16Z" fill="currentColor" />
  </svg>
);

const MuteIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    aria-hidden
    fill="none"
    height="1em"
    viewBox="0 0 32 32"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <title>Mute</title>
    <rect fill="currentColor" height="8" width="5" x="6.5" y="12" />
    <path d="M13.5 16L22.5 10.8038V21.1962L13.5 16Z" fill="currentColor" />
  </svg>
);

export type AudioPlayerProps = Omit<
  ComponentProps<typeof MediaController>,
  "audio"
>;

export const AudioPlayer = ({
  children,
  className,
  style,
  ...props
}: AudioPlayerProps) => (
  <MediaController
    audio
    data-slot="audio-player"
    className={cn("overflow-visible", className)}
    style={
      {
        "--media-background-color": "transparent",
        "--media-button-icon-height": "1rem",
        "--media-button-icon-width": "1rem",
        "--media-control-background": "transparent",
        "--media-control-hover-background": "var(--color-accent)",
        "--media-control-padding": "0",
        "--media-font": "var(--font-mono)",
        "--media-font-size": "14px",
        "--media-icon-color": "currentColor",
        "--media-preview-time-background": "var(--color-background)",
        "--media-preview-time-border-radius": "var(--radius-md)",
        "--media-preview-time-text-shadow": "none",
        "--media-primary-color": "var(--color-primary)",
        "--media-range-bar-color": "var(--color-primary)",
        "--media-range-track-background": "var(--color-muted)",
        "--media-range-track-height": "6px",
        "--media-range-thumb-background": "var(--color-primary)",
        "--media-range-thumb-height": "12px",
        "--media-range-thumb-width": "12px",
        "--media-secondary-color": "var(--color-muted)",
        "--media-text-color": "var(--color-muted-foreground)",
        "--media-tooltip-arrow-display": "none",
        "--media-tooltip-background": "var(--color-background)",
        "--media-tooltip-border-radius": "var(--radius-md)",
        ...style,
      } as CSSProperties
    }
    {...props}
  >
    {children}
  </MediaController>
);

export type AudioPlayerElementProps = Omit<ComponentProps<"audio">, "src"> &
  (
    | {
        data: SpeechResult["audio"];
      }
    | {
        src: string;
      }
  );

// Patched from upstream: branch on the discriminator so `data` is
// destructured out of the spread. Upstream computed src from `data`
// then spread `{...props}` whole, which leaked `data="[object Object]"`
// onto the <audio> element and triggered React warnings.
export const AudioPlayerElement = (props: AudioPlayerElementProps) => {
  if ("src" in props) {
    return (
      // oxlint-disable-next-line eslint-plugin-jsx-a11y(media-has-caption) -- audio player captions are provided by consumer
      <audio data-slot="audio-player-element" slot="media" {...props} />
    );
  }
  const { data, ...rest } = props;
  return (
    // oxlint-disable-next-line eslint-plugin-jsx-a11y(media-has-caption) -- audio player captions are provided by consumer
    <audio
      data-slot="audio-player-element"
      slot="media"
      src={`data:${data.mediaType};base64,${data.base64}`}
      {...rest}
    />
  );
};

export type AudioPlayerControlBarProps = ComponentProps<typeof MediaControlBar>;

export const AudioPlayerControlBar = ({
  children,
  className,
  ...props
}: AudioPlayerControlBarProps) => (
  <MediaControlBar
    data-slot="audio-player-control-bar"
    className={cn(
      "relative flex w-full items-center gap-2 rounded-[14px] border border-border bg-card px-3 py-2 [&>*]:m-0 [&>*]:shrink-0",
      "[&_[data-slot=audio-player-time-range]]:min-w-0 [&_[data-slot=audio-player-time-range]]:flex-1",
      "overflow-visible",
      className,
    )}
    {...props}
  >
    {children}
  </MediaControlBar>
);

export type AudioPlayerPlayButtonProps = ComponentProps<typeof MediaPlayButton>;

export const AudioPlayerPlayButton = ({
  className,
  ...props
}: AudioPlayerPlayButtonProps) => (
  <Button
    size="icon-sm"
    variant="ghost"
    render={
      <MediaPlayButton
        className={className}
        data-slot="audio-player-play-button"
        {...props}
      >
        <span slot="play">
          <Icon icon={Play} weight="fill" />
        </span>
        <span slot="pause">
          <Icon icon={Pause} weight="fill" />
        </span>
      </MediaPlayButton>
    }
  ></Button>
);

export type AudioPlayerSeekBackwardButtonProps = ComponentProps<
  typeof MediaSeekBackwardButton
>;

export const AudioPlayerSeekBackwardButton = ({
  seekOffset = 10,
  ...props
}: AudioPlayerSeekBackwardButtonProps) => (
  <Button
    size="icon-sm"
    variant="ghost"
    render={
      <MediaSeekBackwardButton
        data-slot="audio-player-seek-backward-button"
        seekOffset={seekOffset}
        {...props}
      >
        <SeekBackwardIcon className="size-9" slot="icon" />
      </MediaSeekBackwardButton>
    }
  ></Button>
);

export type AudioPlayerSeekForwardButtonProps = ComponentProps<
  typeof MediaSeekForwardButton
>;

export const AudioPlayerSeekForwardButton = ({
  seekOffset = 10,
  ...props
}: AudioPlayerSeekForwardButtonProps) => (
  <Button
    size="icon-sm"
    variant="ghost"
    render={
      <MediaSeekForwardButton
        data-slot="audio-player-seek-forward-button"
        seekOffset={seekOffset}
        {...props}
      >
        <SeekForwardIcon className="size-9" slot="icon" />
      </MediaSeekForwardButton>
    }
  ></Button>
);

export type AudioPlayerTimeDisplayProps = ComponentProps<
  typeof MediaTimeDisplay
>;

export const AudioPlayerTimeDisplay = ({
  className,
  ...props
}: AudioPlayerTimeDisplayProps) => (
  <MediaTimeDisplay
    className={cn(
      "flex shrink-0 items-center tabular-nums text-muted-foreground",
      className,
    )}
    data-slot="audio-player-time-display"
    {...props}
  />
);

export type AudioPlayerTimeRangeProps = ComponentProps<typeof MediaTimeRange>;

export const AudioPlayerTimeRange = ({
  className,
  style,
  ...props
}: AudioPlayerTimeRangeProps) => (
  <MediaTimeRange
    className={cn("min-w-0 flex-1", className)}
    data-slot="audio-player-time-range"
    style={
      {
        "--media-range-track-height": "6px",
        "--media-range-track-background": "var(--color-muted)",
        "--media-range-bar-color": "var(--color-primary)",
        "--media-range-thumb-height": "12px",
        "--media-range-thumb-width": "12px",
        "--media-range-thumb-background": "var(--color-primary)",
        ...style,
      } as CSSProperties
    }
    {...props}
  />
);

export type AudioPlayerDurationDisplayProps = ComponentProps<
  typeof MediaDurationDisplay
>;

export const AudioPlayerDurationDisplay = ({
  className,
  ...props
}: AudioPlayerDurationDisplayProps) => (
  <MediaDurationDisplay
    className={cn(
      "flex shrink-0 items-center tabular-nums text-muted-foreground",
      className,
    )}
    data-slot="audio-player-duration-display"
    {...props}
  />
);

export type AudioPlayerMuteButtonProps = ComponentProps<typeof MediaMuteButton>;

export const AudioPlayerMuteButton = ({
  className,
  ...props
}: AudioPlayerMuteButtonProps) => (
  <Button
    size="icon-sm"
    variant="ghost"
    render={
      <MediaMuteButton
        className={className}
        data-slot="audio-player-mute-button"
        {...props}
      >
        <MuteIcon className="size-9" slot="icon" />
        <MuteIcon className="size-9" slot="off" />
        <MuteIcon className="size-9" slot="low" />
        <MuteIcon className="size-9" slot="medium" />
        <MuteIcon className="size-9" slot="high" />
      </MediaMuteButton>
    }
  ></Button>
);

export type AudioPlayerVolumeRangeProps = ComponentProps<
  typeof MediaVolumeRange
>;

export const AudioPlayerVolumeRange = ({
  className,
  style,
  ...props
}: AudioPlayerVolumeRangeProps) => (
  <MediaVolumeRange
    className={cn("w-16", className)}
    data-slot="audio-player-volume-range"
    style={
      {
        "--media-range-track-height": "6px",
        "--media-range-track-background": "var(--color-muted)",
        "--media-range-bar-color": "var(--color-primary)",
        "--media-range-thumb-height": "12px",
        "--media-range-thumb-width": "12px",
        "--media-range-thumb-background": "var(--color-primary)",
        ...style,
      } as CSSProperties
    }
    {...props}
  />
);
